import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AiOutlineProduct } from "react-icons/ai";
import { RiShoppingCartLine, RiHome9Line, RiLogoutBoxLine } from "react-icons/ri";
import { IoReceiptOutline, IoPersonOutline } from "react-icons/io5";
import { LayoutDashboard } from "lucide-react";
import { useI18n } from '@/i18n';
import { useAuthStore } from '@/store/authStore';
import { useCartStore } from '@/store/cartStore';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export function DesktopSidebar() {
    const { t } = useI18n();
    const location = useLocation();
    const navigate = useNavigate();
    const { profile, signOut, isAdmin } = useAuthStore();
    const cartCount = useCartStore((state) => state.getTotalItems());

    const handleSignOut = async () => {
        await signOut();
        navigate('/');
    };

    const navItems = [
        { href: '/', label: t('nav.home'), icon: RiHome9Line },
        { href: '/products', label: t('nav.products'), icon: AiOutlineProduct },
        { href: '/cart', label: t('nav.cart'), icon: RiShoppingCartLine, count: cartCount },
        { href: '/orders', label: t('nav.orders'), icon: IoReceiptOutline },
        { href: '/account', label: t('nav.profile'), icon: IoPersonOutline },
    ];

    if (isAdmin()) {
        navItems.push({ href: '/admin', label: t('nav.admin'), icon: LayoutDashboard });
    }

    return (
        <aside className="hidden lg:flex flex-col w-64 h-screen bg-white border-r sticky top-0 left-0 z-50 shrink-0">
            {/* Logo Section */}
            <div className="p-6 mb-2">
                <Link to="/" className="flex items-center gap-3 group">
                    <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center overflow-hidden shadow-lg shadow-primary/20 group-hover:scale-105 transition-transform">
                        <img src="/icons/logo-3.png" alt="Logo" className="w-full h-full object-cover" />
                    </div>
                    <span className="text-xl font-black text-primary tracking-tight">
                        {t('app.name')}
                    </span>
                </Link>
            </div>

            {/* Navigation Items */}
            <nav className="flex-1 px-4 space-y-1.5 overflow-y-auto scrollbar-none no-scrollbar">
                {navItems.map((item) => {
                    const active = item.href === '/'
                        ? location.pathname === '/'
                        : location.pathname.startsWith(item.href) && item.href !== '/';

                    return (
                        <Link
                            key={item.href}
                            to={item.href}
                            className={cn(
                                "flex items-center gap-3 px-4 py-2 rounded-2xl transition-all duration-200 group relative",
                                active 
                                    ? "bg-primary/5 text-primary" 
                                    : "text-gray-600 hover:bg-primary/5 hover:text-primary"
                            )}
                        >
                            <div className={cn(
                                "flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300",
                                active ? "chocolate-beveled text-white shadow-md" : "bg-transparent text-gray-600 group-hover:text-primary"
                            )}>
                                <item.icon className={cn("w-5 h-5", active ? "text-white" : "")} />
                            </div>
                            <span className="font-bold text-[15px]">{item.label}</span>
                            {item.count !== undefined && item.count > 0 && (
                                <span className={cn(
                                    "ml-auto w-5 h-5 rounded-full text-[10px] flex items-center justify-center font-black",
                                    active ? "bg-primary text-white" : "bg-primary text-white"
                                )}>
                                    {item.count > 99 ? '99+' : item.count}
                                </span>
                            )}
                        </Link>
                    );
                })}
            </nav>

            {/* User Profile & Logout */}
            <div className="p-4 mt-auto border-t border-gray-100/80 bg-gray-50/50">
                <div className="flex items-center gap-3 p-3 bg-white rounded-2xl border border-gray-100 shadow-sm mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg shrink-0">
                        {profile?.full_name?.[0] || 'U'}
                    </div>
                    <div className="flex flex-col min-w-0">
                        <span className="text-sm font-bold text-black truncate">
                            {profile?.full_name || profile?.phone || 'User'}
                        </span>
                        <span className="text-[10px] text-gray-700 truncate uppercase tracking-wider font-semibold">
                            {isAdmin() ? t('nav.admin') : t('nav.profile')}
                        </span>
                    </div>
                </div>
                <Button 
                    variant="ghost" 
                    className="w-full justify-start gap-3 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-2xl h-11 transition-colors px-4"
                    onClick={handleSignOut}
                >
                    <RiLogoutBoxLine className="w-5 h-5" />
                    <span className="font-bold text-sm">{t('nav.logout')}</span>
                </Button>
            </div>
        </aside>
    );
}
